import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  RefreshCw, Download, Play, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, XCircle, Clock, Database, Filter,
} from 'lucide-react';

import { PreCanonicalPanel } from './PreCanonicalPanel';
import { CanonicalDetectShadowPanel } from './CanonicalDetectShadowPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const OUTCOME_STYLES = {
  MERGE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  INSUFFICIENT_EVIDENCE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  DIFFERENT_RECORDINGS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  ROLLED_BACK: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  ERROR: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const CHANNEL_LABELS = {
  fingerprint_clash: '🔍 Fingerprint Clash',
  normalized_key_drift: '🧭 Normalized-Key Drift',
  fuzzy_artist: '📝 Fuzzy Artist',
  shared_known_key: '🔗 Shared Known Key',
};

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('el-GR', {
      dateStyle: 'short', timeStyle: 'medium',
    });
  } catch { return iso; }
}

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const StatPill = ({ label, value, tone = 'default', testId }) => {
  const toneClass = {
    default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    good: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  }[tone];
  return (
    <div className={`px-3 py-2 rounded-lg ${toneClass}`} data-testid={testId}>
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="text-2xl font-bold leading-tight">{value}</div>
    </div>
  );
};

const AuditRow = ({ row, onRollback, onExpand, expanded }) => {
  const va = row.variant_a || {};
  const vb = row.variant_b || {};
  const isrc = row.target_isrc;
  return (
    <div
      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      data-testid={`llm-dedup-audit-row-${row.merge_id}`}
    >
      <div
        className="flex items-start justify-between gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
        onClick={() => onExpand(row.merge_id)}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {expanded ? <ChevronDown className="w-4 h-4 mt-1 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${OUTCOME_STYLES[row.outcome] || 'bg-gray-100'}`}>
                {row.outcome}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {CHANNEL_LABELS[row.detection_channel] || row.detection_channel}
              </span>
              <span className="text-xs text-gray-400">{row.mode}</span>
              {isrc && (
                <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 font-mono">
                  ISRC {isrc}
                </span>
              )}
              {row.winning_source && (
                <span className="text-xs text-gray-500">via {row.winning_source}</span>
              )}
            </div>
            <div className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">
              <span className="text-gray-500">A:</span> {va.canonical_artist || '?'} / <span className="italic">{va.canonical_title || '?'}</span>
              <span className="text-gray-400 mx-2">↔</span>
              <span className="text-gray-500">B:</span> {vb.canonical_artist || '?'} / <span className="italic">{vb.canonical_title || '?'}</span>
            </div>
            {row.target_artist && (
              <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                → target: <b>{row.target_artist}</b> / <i>{row.target_title}</i>
              </div>
            )}
            {row.reason && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                {row.reason}
              </div>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-gray-400">{formatTs(row.applied_at)}</div>
          {row.action === 'live_merged' && row.records_modified > 0 && (
            <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
              {row.records_modified} rows
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="font-semibold text-gray-500 mb-1">Variant A</div>
              <pre className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">{JSON.stringify(va, null, 2)}</pre>
            </div>
            <div>
              <div className="font-semibold text-gray-500 mb-1">Variant B</div>
              <pre className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">{JSON.stringify(vb, null, 2)}</pre>
            </div>
          </div>
          {row.evidence && (
            <div className="text-xs">
              <div className="font-semibold text-gray-500 mb-1">Evidence (external APIs)</div>
              <pre className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-h-64">{JSON.stringify(row.evidence, null, 2)}</pre>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => downloadJson(row, `merge-${row.merge_id}.json`)}
              className="text-xs px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 flex items-center gap-1"
              data-testid={`llm-dedup-export-single-${row.merge_id}`}
            >
              <Download className="w-3 h-3" /> Export JSON
            </button>
            {row.action === 'live_merged' && !row.rolled_back_at && (
              <button
                onClick={() => onRollback(row.merge_id)}
                className="text-xs px-3 py-1.5 rounded bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/40 dark:hover:bg-red-900/60 dark:text-red-200 flex items-center gap-1"
                data-testid={`llm-dedup-rollback-${row.merge_id}`}
              >
                <XCircle className="w-3 h-3" /> Rollback
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LLMDedupReportPanel = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [digest, setDigest] = useState(null);
  const [runs, setRuns] = useState([]);
  const [audit, setAudit] = useState([]);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');  // YYYY-MM-DD
  const [expandedId, setExpandedId] = useState(null);

  const authConfig = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dateQ = dateFilter ? `?date=${dateFilter}` : '';
      const outcomeQ = outcomeFilter ? `&outcome=${outcomeFilter}` : '';
      const [cfgRes, digRes, runRes, audRes] = await Promise.all([
        axios.get(`${API}/api/admin/llm-dedup/config`, authConfig),
        axios.get(`${API}/api/admin/llm-dedup/digest${dateQ}`, authConfig),
        axios.get(`${API}/api/admin/llm-dedup/last-run?limit=10`, authConfig),
        axios.get(`${API}/api/admin/llm-dedup/audit?limit=200${outcomeQ}`, authConfig),
      ]);
      setConfig(cfgRes.data);
      setDigest(digRes.data);
      setRuns(runRes.data.rows || []);
      setAudit(audRes.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [dateFilter, outcomeFilter, authConfig]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runPoll = useRef({});
  const stopRunPoll = () => {
    clearInterval(runPoll.current.interval);
    clearTimeout(runPoll.current.safety);
    runPoll.current = {};
  };
  useEffect(() => () => stopRunPoll(), []);

  const triggerRunNow = async (shadowFlag) => {
    if (triggering) return;
    if (!shadowFlag && !window.confirm('LIVE MODE: αυτό θα εφαρμόσει τα merges στη βάση. Είσαι σίγουρος;')) return;
    setTriggering(true);
    try {
      const url = `${API}/api/admin/llm-dedup/run-now?shadow=${shadowFlag}`;
      await axios.post(url, {}, authConfig);
      // Poll every 8s for up to 15min; cleared on unmount
      stopRunPoll();
      runPoll.current.interval = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/admin/llm-dedup/last-run?limit=1`, authConfig);
          const last = r.data.rows?.[0];
          if (last && last.status !== 'started' && last.status !== 'in_progress') {
            stopRunPoll();
            await loadAll();
            setTriggering(false);
          }
        } catch { /* keep polling */ }
      }, 8000);
      runPoll.current.safety = setTimeout(() => { stopRunPoll(); setTriggering(false); }, 15 * 60 * 1000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setTriggering(false);
    }
  };

  const rollback = async (mergeId) => {
    if (!window.confirm(`Rollback merge ${mergeId}? Αυτό θα επαναφέρει τα ονόματα στα stream_songs.`)) return;
    try {
      await axios.post(`${API}/api/admin/llm-dedup/rollback/${mergeId}`, {}, authConfig);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const exportFullReport = () => {
    downloadJson({
      exported_at: new Date().toISOString(),
      backend_url: API,
      config,
      digest,
      last_runs: runs,
      audit_rows: audit,
    }, `llm-dedup-report-${new Date().toISOString().slice(0, 10)}.json`);
  };

  const runsByStatus = useMemo(() => {
    const s = { ok: 0, error: 0, cancelled: 0, aborted: 0, in_progress: 0, other: 0 };
    runs.forEach((r) => {
      const st = (r.status || '').toLowerCase();
      if (st === 'ok') s.ok++;
      else if (st === 'error') s.error++;
      else if (st === 'cancelled') s.cancelled++;
      else if (st.includes('abort')) s.aborted++;
      else if (st === 'in_progress' || st === 'started') s.in_progress++;
      else s.other++;
    });
    return s;
  }, [runs]);

  const auditByChannel = useMemo(() => {
    const c = {};
    audit.forEach((r) => {
      const ch = r.detection_channel || 'unknown';
      c[ch] = (c[ch] || 0) + 1;
    });
    return c;
  }, [audit]);

  return (
    <div className="space-y-4" data-testid="llm-dedup-report-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Database className="w-5 h-5 text-purple-600" />
            LLM Artist/Title Dedup Report
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Αυτόνομο σύστημα ενοποίησης παραλλαγών (Konstantinos Argiros/Argyros, BOM chars, transliterations) με ISRC verification μέσω iTunes / Deezer / MusicBrainz.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadAll}
            disabled={loading}
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
            data-testid="llm-dedup-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </button>
          <button
            onClick={exportFullReport}
            className="px-3 py-2 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 dark:text-blue-200 text-sm font-medium flex items-center gap-1.5"
            data-testid="llm-dedup-export-report-btn"
          >
            <Download className="w-4 h-4" />
            Εξαγωγή JSON
          </button>
          <button
            onClick={() => triggerRunNow(true)}
            disabled={triggering}
            className="px-3 py-2 rounded bg-amber-100 hover:bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 dark:text-amber-200 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
            data-testid="llm-dedup-run-shadow-btn"
          >
            <Play className={`w-4 h-4 ${triggering ? 'animate-pulse' : ''}`} />
            {triggering ? 'Τρέχει...' : 'Shadow Run'}
          </button>
          {config?.shadow_mode === false && (
            <button
              onClick={() => triggerRunNow(false)}
              disabled={triggering}
              className="px-3 py-2 rounded bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/40 dark:hover:bg-red-900/60 dark:text-red-200 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
              data-testid="llm-dedup-run-live-btn"
            >
              <Play className="w-4 h-4" />
              LIVE Run
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 flex items-start gap-2" data-testid="llm-dedup-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="text-sm">{String(error)}</div>
        </div>
      )}

      {/* Config strip */}
      {config && (
        <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 flex flex-wrap gap-4 text-xs" data-testid="llm-dedup-config-strip">
          <span><b>Enabled:</b> {config.enabled ? '✅' : '❌'}</span>
          <span><b>Mode:</b> {config.shadow_mode ? '🟡 Shadow (dry-run)' : '🔴 LIVE (writes DB)'}</span>
          <span><b>Window:</b> {config.window}</span>
          <span><b>Batch:</b> {config.batch_limit}</span>
          <span><b>Wall-clock:</b> {config.wall_clock_budget_sec}s</span>
          <span><b>Min plays:</b> {config.min_plays_per_variant}</span>
          <span><b>Fuzzy ≤:</b> {config.fuzzy_distance_max}</span>
        </div>
      )}

      <PreCanonicalPanel authConfig={authConfig} />
      <CanonicalDetectShadowPanel authConfig={authConfig} />

      {/* Digest counts */}
      {digest && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Ημερήσιο Digest — {digest.date}
            </h4>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              data-testid="llm-dedup-date-filter"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <StatPill label="Shadow proposed" value={digest.counts?.shadow_proposed || 0} tone="warn" testId="stat-shadow-proposed" />
            <StatPill label="Live applied" value={digest.counts?.applied || 0} tone="good" testId="stat-applied" />
            <StatPill label="Skipped" value={digest.counts?.skipped || 0} tone="default" testId="stat-skipped" />
            <StatPill label="Rolled back" value={digest.counts?.rolled_back || 0} tone={digest.counts?.rolled_back ? 'bad' : 'default'} testId="stat-rolled-back" />
            <StatPill label="Σύνολο events" value={digest.total_events || 0} tone="info" testId="stat-total" />
          </div>
        </div>
      )}

      {/* Runs (health) */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
          <Clock className="w-4 h-4" />
          Τελευταία Runs & Αποτυχίες
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
          <StatPill label="✅ OK" value={runsByStatus.ok} tone="good" testId="run-stat-ok" />
          <StatPill label="⏳ In progress" value={runsByStatus.in_progress} tone="info" testId="run-stat-inprogress" />
          <StatPill label="⏸ Aborted (window)" value={runsByStatus.aborted} tone="warn" testId="run-stat-aborted" />
          <StatPill label="❌ Error" value={runsByStatus.error} tone={runsByStatus.error ? 'bad' : 'default'} testId="run-stat-error" />
          <StatPill label="🚫 Cancelled" value={runsByStatus.cancelled} tone={runsByStatus.cancelled ? 'bad' : 'default'} testId="run-stat-cancelled" />
          <StatPill label="? Other" value={runsByStatus.other} tone="default" testId="run-stat-other" />
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="llm-dedup-runs-list">
          {runs.length === 0 ? (
            <div className="text-sm text-gray-500 italic">Καμία εγγραφή.</div>
          ) : runs.map((r) => {
            const statusTone = r.status === 'ok' ? 'text-emerald-600'
              : r.status === 'error' || r.status === 'cancelled' ? 'text-red-600'
              : (r.status || '').includes('abort') ? 'text-amber-600'
              : 'text-blue-600';
            return (
              <div key={r.run_id + r.logged_at} className="flex items-start justify-between gap-2 text-xs p-2 rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" data-testid={`llm-dedup-run-${r.run_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{r.run_id}</span>
                    <span className={`font-semibold ${statusTone}`}>{r.status}</span>
                    {r.trigger && <span className="text-gray-500">({r.trigger})</span>}
                    {r.shadow_mode !== undefined && (
                      <span className="text-gray-500">{r.shadow_mode ? 'shadow' : 'LIVE'}</span>
                    )}
                  </div>
                  {r.reason && <div className="text-gray-500 dark:text-gray-400 mt-0.5 break-words">{r.reason}</div>}
                  {(r.detected !== undefined || r.merges_shadow_proposed !== undefined || r.merges_applied !== undefined) && (
                    <div className="text-gray-600 dark:text-gray-300 mt-0.5">
                      {r.detected !== undefined && <>detected: <b>{r.detected}</b> · </>}
                      {r.verified !== undefined && <>verified: <b>{r.verified}</b> · </>}
                      {r.merges_shadow_proposed !== undefined && <>shadow: <b>{r.merges_shadow_proposed}</b> · </>}
                      {r.merges_applied !== undefined && <>live: <b>{r.merges_applied}</b> · </>}
                      {r.skipped_insufficient !== undefined && <>insufficient: <b>{r.skipped_insufficient}</b> · </>}
                      {r.errors !== undefined && <>errors: <b>{r.errors}</b></>}
                    </div>
                  )}
                </div>
                <div className="text-right text-gray-400 flex-shrink-0">
                  <div>{formatTs(r.logged_at)}</div>
                  {r.seconds_elapsed !== undefined && <div>{Math.round(r.seconds_elapsed)}s</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Audit list */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
            <Filter className="w-4 h-4" />
            Audit Log ({audit.length})
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(auditByChannel).map(([ch, n]) => (
              <span key={ch} className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                {CHANNEL_LABELS[ch] || ch}: <b>{n}</b>
              </span>
            ))}
            <select
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              data-testid="llm-dedup-outcome-filter"
            >
              <option value="">Όλα τα outcomes</option>
              <option value="MERGE">MERGE</option>
              <option value="INSUFFICIENT_EVIDENCE">INSUFFICIENT_EVIDENCE</option>
              <option value="DIFFERENT_RECORDINGS">DIFFERENT_RECORDINGS</option>
              <option value="ROLLED_BACK">ROLLED_BACK</option>
              <option value="ERROR">ERROR</option>
            </select>
          </div>
        </div>
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="llm-dedup-audit-list">
          {audit.length === 0 ? (
            <div className="text-sm text-gray-500 italic flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Κανένα audit event με τα τρέχοντα φίλτρα.
            </div>
          ) : audit.map((row) => (
            <AuditRow
              key={row.merge_id}
              row={row}
              expanded={expandedId === row.merge_id}
              onExpand={(id) => setExpandedId(expandedId === id ? null : id)}
              onRollback={rollback}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LLMDedupReportPanel;
